import { CircularProgress, Grid, InputAdornment, TextField } from '@material-ui/core';
import { makeStyles, styled } from '@material-ui/core/styles';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import FilterNoneIcon from '@material-ui/icons/FilterNone';
import { Elements, TextButton } from '@tfo/common';
import { ICompany } from '@tfo/common/src/models';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useIntl } from 'react-intl';
import CustomerPortalDialog from '../../../containers/client/common/CustomerPortalDialog';
import $http from '../../../utils/$http';

const { Paragraph } = Elements

const CopyIcon = styled(FilterNoneIcon)({
  transform: 'scale(0.8)',
})

const RegenerateIcon = styled(AutorenewIcon)({
  transform: 'scale(0.8)',
})

const SelfServiceUrlButton = styled(Elements.Button)({
  display: 'flex',
  alignItems: 'center',
  color: '#0098C3',
  '&:hover': {
    color: '#007A9C',
  },
  '&:active': {
    transform: 'translateY(1px)',
  },
})

const useStyles = makeStyles({

  disclaimer: {
    marginTop: '2rem',
  }
})

type SSOLinkResponse = {
  selfServiceUrl: string
}

type SelfServiceSSOModalProps = {
  open: boolean
  onClose: () => void
  company: ICompany
  isReconfigure?: boolean
}

// Avoids flickering of the loading spinner, 600ms is a magic number that I think looks good
const MinLoadingTime = 600;

const SelfServiceSSOModal: React.FC<SelfServiceSSOModalProps> = observer(({ open, onClose, company, isReconfigure = false }) => {
  const intl = useIntl()
  const styles = useStyles()
  const [ssoLink, setSSOLink] = React.useState<string>('')
  const [generateLink, setGenerateLink] = React.useState(true)
  const [isLoading, setIsLoading] = React.useState(false)

  React.useEffect(() => {
    setIsLoading(true)

    const endpoint = isReconfigure ? '/authenticate/sso/self-service/reconfigure' : '/authenticate/sso/self-service'
    const fetchSSOLink = $http.post<SSOLinkResponse>(endpoint, { companyId: company.id })
    const minWaitTime = new Promise(resolve => setTimeout(resolve, MinLoadingTime))

    Promise.allSettled([fetchSSOLink, minWaitTime])
      .then((values) => {
        if (values[0].status === 'rejected') {
          console.error('Failed to fetch SSO link:', values[0].reason);
          return;
        }

        const selfServiceResponse = values[0].value as SSOLinkResponse
        setSSOLink(selfServiceResponse.selfServiceUrl)
      })
      .finally(() => setIsLoading(false))
  }, [generateLink])

  const toggleGenerateLink = () => {
    setGenerateLink(value => !value)
  }

  const copyLink = () => {
    navigator.clipboard.writeText(ssoLink)
  }

  const titleKey = isReconfigure ? 'sso.selfservice.reconfigure.title' : 'sso.selfservice.title'
  
  return (<CustomerPortalDialog
    title={intl.formatMessage({ id: titleKey }, { companyName: company.name })}
    open={open}
    onClose={onClose}
    content={
      <>
        <Elements.Label >{intl.formatMessage({ id: 'sso.selfservice.selfServiceLink' })}</Elements.Label>
        <TextField
          autoComplete='off'
          fullWidth
          size='small'
          variant='outlined'
          type='password'
          value={ssoLink}
          InputProps={{
            readOnly: true,
            endAdornment: <InputAdornment position='end'>
              {!isLoading &&
                <SelfServiceUrlButton onClick={copyLink} disabled={!ssoLink}>
                  <CopyIcon />{intl.formatMessage({ id: 'sso.selfservice.selfServiceLink.copy' })}
                </SelfServiceUrlButton>}
              {isLoading && <CircularProgress size={'2.5rem'} />}
            </InputAdornment>,
          }}
        />

        <SelfServiceUrlButton onClick={toggleGenerateLink}>
          <RegenerateIcon />{intl.formatMessage({ id: 'sso.selfservice.selfServiceLink.generate' })}
        </SelfServiceUrlButton>

        <Paragraph className={styles.disclaimer}>
          {intl.formatMessage({ id: 'sso.selfservice.disclaimer' })}
        </Paragraph>
      </>
    }
    footer={
      <Grid container spacing={2} justifyContent={'center'}>
        <Grid item xs={6} md={4}>
          <TextButton onClick={onClose} style={{ width: '100%' }}>
            {intl.formatMessage({ id: 'common.SSOModal.close' })}
          </TextButton>
        </Grid>
      </ Grid>
    }
  />)
})

export default SelfServiceSSOModal
